import { useEffect, useState } from 'react'
import {
  Input,
  SubmitButton,
  Typography,
  Popover,
  EyeButton,
  Checkbox,
  Subtitle,
  Title,
} from '~components'
import { useStore, usePathValidation, useNavigate } from '~hooks'
import { string, object, boolean } from '~utils/validation'
import { Values } from './interfaces'
import * as Styled from './styled'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Auth } from '~services'
import { useRequest } from 'ahooks'
import { urlHandler } from '~utils/path'
import { track } from '@amplitude/analytics-browser'

function Password() {
  const defaultValues = {
    password: '',
    remember: false,
  }
  const schema = object({
    password: string()
      .required()
      .min(8, 'Pelo menos 8 caracteres')
      .max(72, 'No máximo 72 caracteres')
      .atLeastOneCharacter('Pelo menos uma letra')
      .atLeastOneNumber('Pelo menos um número'),
    remember: boolean(),
  })
  const { loading, runAsync } = useRequest(Auth.create, { manual: true })
  const termsOfUseUrl = `${process.env.REACT_APP_FRETEBRAS_SITE}/termos-de-uso`
  const privacyPolicyUrl = `${process.env.REACT_APP_FRETEBRAS_SITE}/politica-de-privacidade`
  const [isPasswordVisible, setPasswordVisible] = useState(false)
  const [isPopoverVisible, setPopoverVisible] = useState(false)
  const { cpf } = useStore()
  const navigate = useNavigate()
  const {
    handleSubmit,
    formState: { isValid, dirtyFields, errors },
    register,
    setFocus,
  } = useForm({
    defaultValues,
    mode: 'onChange',
    criteriaMode: 'all',
    resolver: yupResolver(schema),
  })
  const popoverMessages = [
    {
      text: 'Pelo menos 8 caracteres',
      isSuccess: !errors?.password?.types?.min,
    },
    {
      text: 'Pelo menos uma letra',
      isSuccess: !errors?.password?.types?.atLeastOneCharacter,
    },
    {
      text: 'Pelo menos um número',
      isSuccess: !errors?.password?.types?.atLeastOneNumber,
    },
  ]

  usePathValidation(['cpf'])
  useEffect(() => {
    track('perfil_senha_cpf')
  }, [])

  function handleClickShowPassword() {
    setFocus('password')
    setPasswordVisible((prevState) => !prevState)
  }
  function handleChange(event: { target: { value: string } }) {
    const value = event.target.value
    if (value.includes(' ')) event.target.value = value.replace(/ /g, '')
  }
  function handlePaste(event: { preventDefault: () => void }) {
    event.preventDefault()
  }
  function handleFocus() {
    setPopoverVisible(true)
  }
  function handleBlur() {
    setPopoverVisible(false)
  }
  async function onSubmit(values: Values) {
    const token = localStorage.getItem('token')
    await runAsync({ ...values, username: cpf, token })
    navigate('/entrar/placa/sucesso')
  }

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        aria-labelledby="form-plate-login-new-password-title"
      >
        <Title id="form-plate-login-new-password-title">Agora, o acesso é por CPF</Title>
        <Subtitle>
          Crie uma senha para o CPF <Typography as="b">{cpf}</Typography>.
        </Subtitle>
        <Typography as="label">Senha</Typography>
        <Styled.WrapperRelative>
          {dirtyFields?.password && isPopoverVisible && (
            <Popover title="Sua senha deve conter:" messages={popoverMessages} />
          )}
          <Input
            onChange={handleChange}
            onPaste={handlePaste}
            autoFocus
            onFocus={handleFocus}
            onBlur={handleBlur}
            name="password"
            type={isPasswordVisible ? 'text' : 'password'}
            register={register}
            data-testid="password"
            maxLength={72}
          />
          <EyeButton visibility={isPasswordVisible} onClick={handleClickShowPassword} />
        </Styled.WrapperRelative>
        <Checkbox register={register} label="Me manter conectado" name="remember" />
        <Styled.Message>
          Ao entrar você concorda com os{' '}
          <Styled.Link href={urlHandler(termsOfUseUrl)} target="_blank">
            Termos de Uso
          </Styled.Link>{' '}
          e com a{' '}
          <Styled.Link href={urlHandler(privacyPolicyUrl)} target="_blank">
            Política de Privacidade
          </Styled.Link>{' '}
          da Fretebras.
        </Styled.Message>
        <SubmitButton width="100%" disabled={!isValid} loading={loading}>
          Entrar
        </SubmitButton>
      </form>
    </>
  )
}

export default Password
